import type { Dayjs } from 'dayjs'

/** Вибраний у фільтрі запис довідника: id для API і підпис для тегу */
export interface ReportOption {
    value: number
    label: string
}

export type ReportSource = 'all' | 'requests' | 'standalone'

export type ReportHired = 'all' | 'true' | 'false'

/** Фільтри-списки звіту; ключ — назва query-параметра API */
export const REPORT_LIST_FILTERS = [
    'carrier',
    'driver',
    'vehicle',
    'vehicleType',
    'cargo',
    'constructionObject',
    'loadingPoint',
    'unloadingPoint',
    'typicalRoute',
    'dispatcher',
] as const

export type ReportListFilter = (typeof REPORT_LIST_FILTERS)[number]

export interface ReportFilterState {
    /** Період за датою підтвердження диспетчером; null — за весь час */
    period: [Dayjs, Dayjs] | null
    source: ReportSource
    isHired: ReportHired
    lists: Record<ReportListFilter, ReportOption[]>
}

export type CompletionGroupBy = 'driver' | 'vehicle' | 'carrier' | 'dispatcher' | 'day' | 'week' | 'month'

export type RatingGroupBy = 'dispatcher' | 'driver'

export type ReportGroupBy = CompletionGroupBy | RatingGroupBy

/** Рядок групи з API: ключ (id або дата початку періоду), назва, uuid сутності */
export interface ReportGroupRow {
    key: string | null
    label: string | null
    uuid: string | null
}

export interface CompletionMetrics {
    tasks: number
    trips: number
    tonnes: number
    delayed: number
    ratingsCount: number
    averageRating: number | null
}

export type CompletionRow = ReportGroupRow & CompletionMetrics

export interface CompletionReport {
    items: CompletionRow[]
    totals: CompletionMetrics
}

export interface CompletionAssignment {
    id: number
    uuid: string
    confirmedAt: string | null
    driverName: string | null
    vehicleLabel: string | null
    trips: number | null
    tonnes: number | null
    hasDelay: boolean
    ratingsCount: number
    averageRating: number | null
}

/** Кількість оцінок кожного бала: ключі '1'…'5' */
export type RatingDistribution = Record<'1' | '2' | '3' | '4' | '5', number>

export interface RatingMetrics {
    tasks: number
    ratingsCount: number
    averageRating: number | null
    distribution: RatingDistribution
}

export type RatingRow = ReportGroupRow & RatingMetrics

export interface RatingReport {
    items: RatingRow[]
    totals: RatingMetrics
}

export interface RatingItem {
    id: number
    ratingDate: string | null
    rating: number
    comment: string | null
    assignmentId: number
    assignmentUuid: string
    driverName: string | null
    dispatcherName: string | null
}

/** «Контроль пробігу»: межі відхилення — у км або % від пробігу Wialon */
export type MileageUnit = 'km' | 'percent'

/** Статус контролю пробігу зміни (backend MileageDeviationStatus) */
export type MileageControlStatus = 'ok' | 'deviation' | 'reviewed'

/** Фільтри-довідники «Контролю пробігу» (назви query-параметрів API) */
export const MILEAGE_LIST_FILTERS = ['driver', 'vehicle', 'carrier'] as const

export type MileageListFilter = (typeof MILEAGE_LIST_FILTERS)[number]

export interface MileageFilterState {
    /** За датою зміни; обов'язковий */
    period: [Dayjs, Dayjs]
    lists: Record<MileageListFilter, ReportOption[]>
    statuses: MileageControlStatus[]
    unit: MileageUnit
    /** Відхилення зміни за модулем більше ніж (строго); null — без межі */
    deviationMin: number | null
    /** Відхилення зміни за модулем менше ніж (строго); null — без межі */
    deviationMax: number | null
}

export interface MileageMetrics {
    shifts: number
    /** Зміни без пробігу Wialon чи одометра — у суми не входять */
    shiftsWithoutData: number
    /** Пробіг за одометром, км */
    mileage: number
    wialonMileage: number
    /** Одометр − Wialon, км (зі знаком) */
    deviation: number
    /** Відхилення у % від Wialon; null — порівнювати нема з чим */
    deviationPercent: number | null
    /** Змін зі статусом «Потребує перевірки» */
    awaitingReview: number
}

export type MileageRow = ReportGroupRow & MileageMetrics

export interface MileageReport {
    items: MileageRow[]
    totals: MileageMetrics
}

/** Зміна в деталізації «Контролю пробігу» */
export interface MileageShift {
    id: number
    uuid: string
    code: string
    shiftDate: string
    vehicleLabel: string | null
    startOdometer: number | null
    endOdometer: number | null
    mileage: number | null
    wialonMileage: number | null
    deviation: number | null
    deviationPercent: number | null
    controlStatus: MileageControlStatus | null
}
